<template>
  <div class="metric-rows-editor">
    <Alert
      v-if="rowsError"
      variant="error"
      :message="rowsError"
      data-testid="metrics-rows-error"
      class="metrics-error"
    />

    <draggable
      :value="rows"
      handle=".metric-row__handle"
      class="metrics-list"
      @input="handleReorder"
    >
      <MetricRow
        v-for="(row, index) in rows"
        :key="row.rowId"
        :index="index"
        :column-count="row.columnCount"
        :columns="row.columns"
        @update:columnCount="setColumnCount(index, $event)"
        @delete="deleteRow(index)"
        @edit-metric="openMetricEditor(index, $event)"
        @delete-metric="deleteMetric(index, $event)"
      />
    </draggable>

    <AddMetricSideSheet
      v-model="showMetricSideSheet"
      :column="editingColumn"
      :metric-options="metricOptions"
      :inform-program-id="informProgramId"
      :metric-options-error="metricOptionsError"
      :metric-options-loaded="metricOptionsLoaded"
      :metric-options-reason="metricOptionsReason"
      @submit="handleMetricSubmit"
    />
  </div>
</template>

<script>
import draggable from "vuedraggable";
import Alert from "@/components/generic/Alert.vue";
import MetricRow from "./MetricRow.vue";
import AddMetricSideSheet from "./AddMetricSideSheet/AddMetricSideSheet.vue";

// Shared row/metric CRUD + validation for Summary Card Metrics and Detail Page Metrics.
export default {
  name: "MetricRowsEditor",
  components: { draggable, Alert, MetricRow, AddMetricSideSheet },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    metricOptions: { type: Array, default: () => [] },
    informProgramId: { type: String, default: null },
    metricOptionsError: { type: Boolean, default: false },
    metricOptionsLoaded: { type: Boolean, default: false },
    metricOptionsReason: { type: String, default: null },
    // Summary Card Metrics requires a configured metric in addition to a row; Detail does not.
    requireConfiguredMetric: { type: Boolean, default: false },
  },
  emits: ["update:rows"],
  data() {
    return {
      rowsError: null,
      showMetricSideSheet: false,
      editingSlot: null,
    };
  },
  computed: {
    editingColumn() {
      if (!this.editingSlot) return null;
      const { rowIndex, slotIndex } = this.editingSlot;
      return this.rows[rowIndex]?.columns?.find((column) => column.slotIndex === slotIndex) ?? null;
    },
  },
  methods: {
    openMetricEditor(rowIndex, slotIndex) {
      this.editingSlot = { rowIndex, slotIndex };
      this.showMetricSideSheet = true;
    },
    handleMetricSubmit(column) {
      if (!this.editingSlot) return;
      const { rowIndex, slotIndex } = this.editingSlot;
      const submitted = { ...column, slotIndex };
      this.rowsError = null;
      this.$emit(
        "update:rows",
        this.rows.map((row, index) => {
          if (index !== rowIndex) return row;
          const columns = row.columns ?? [];
          return {
            ...row,
            columns: columns.some((item) => item.slotIndex === slotIndex)
              ? columns.map((item) => (item.slotIndex === slotIndex ? submitted : item))
              : [...columns, submitted],
          };
        })
      );
    },
    deleteMetric(rowIndex, slotIndex) {
      this.$emit(
        "update:rows",
        this.rows.map((row, index) =>
          index === rowIndex
            ? {
                ...row,
                columns: (row.columns ?? []).filter((column) => column.slotIndex !== slotIndex),
              }
            : row
        )
      );
    },
    nextRowId() {
      return this.rows.reduce((min, row) => Math.min(min, row.rowId ?? 0), 0) - 1;
    },
    addRow() {
      this.$emit("update:rows", [
        ...this.rows,
        { rowId: this.nextRowId(), columnCount: 1, columns: [] },
      ]);
      this.rowsError = null;
    },
    deleteRow(index) {
      this.$emit(
        "update:rows",
        this.rows.filter((_, rowIndex) => rowIndex !== index)
      );
    },
    setColumnCount(index, columnCount) {
      this.$emit(
        "update:rows",
        this.rows.map((row, rowIndex) => (rowIndex === index ? { ...row, columnCount } : row))
      );
    },
    handleReorder(reordered) {
      this.$emit("update:rows", reordered);
    },
    validate() {
      if (this.rows.length === 0) {
        this.rowsError = "At least one row is required";
        return false;
      }
      if (this.requireConfiguredMetric) {
        const hasConfiguredMetric = this.rows.some((row) =>
          (row.columns ?? []).some(
            (column) => !!column.componentType && column.slotIndex < row.columnCount
          )
        );
        if (!hasConfiguredMetric) {
          this.rowsError = "At least one metric is required";
          return false;
        }
      }
      this.rowsError = null;
      return true;
    },
  },
};
</script>

<style scoped>
.metric-rows-editor {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.metrics-error {
  width: 100%;
}

.metrics-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
